// DEPLOY-PROBE-bump

/**
 * UserManagementMain Component
 * 
 * Main component for managing employees and construction workers in the payslip system.
 * Provides a tabbed interface for managing both office employees (本社) and construction
 * workers (現場) with search, add, edit, and delete functionality.
 * 
 * Features:
 * - Dual worker type tabs: Office employees (本社) and Construction workers (現場)
 * - Search functionality for both employee types
 * - Add new employees/construction workers via modal
 * - Edit existing employees/construction workers
 * - Delete employees/construction workers with confirmation
 * - Responsive design with separate mobile and desktop layouts
 * - Real-time search results display
 * - Form validation and error handling
 * - Japanese language interface
 * 
 * State Management:
 * - Separate state for office employees and construction workers
 * - Modal states for add/edit operations
 * - Loading states for async operations
 * - Search query and results states
 * 
 * Used by:
 * - MainLayout component as a main page component
 * 
 * @component
 */

import React, { useState, useEffect, useMemo } from 'react';
import './css/UserManagementMain.css';
import { AddNewUserModal } from './components/AddNew/AddNewUserModal';
import { ConstructionWorkerModal } from './components/AddNew/ConstructionWorkerModal';
import {
  UnifiedRegisterModal,
  type UnifiedRegisterFormData,
  OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN,
} from './components/AddNew/UnifiedRegisterModal';
import { UnifiedUserSearchResults, type UnifiedUserRow } from './components/Search/UnifiedUserSearchResults';
import { DeleteConfirmModal } from './components/DeleteConfirmModal';
import EmployeeService, { Employee } from '../../services/EmployeeService';
import ConstructionWorkerService, { ConstructionWorker } from '../../services/ConstructionWorkerService';

/**
 * UserManagementMain Component
 * 
 * Main container component for user management operations.
 * Manages both office employees and construction workers with full CRUD operations.
 * 
 * @returns React functional component
 */
export const UserManagementMain: React.FC<{ isConstructionAdminOnly?: boolean }> = ({ isConstructionAdminOnly = false }) => {
  /**
   * State Management:
   * 
   * Layout and Navigation:
   * - isMobile: Boolean indicating if device is mobile (window width <= 768px)
   * 
   * Office Employee States:
   * - searchQuery: Current search query for office employees
   * - searchResults: Array of office employee search results
   * - isSearching: Boolean indicating if office employee search is in progress
   * - isModalOpen: Boolean indicating if office employee modal is open
   * - isLoading: Boolean indicating if office employee operation is loading
   * - modalMode: Current modal mode ('add' or 'edit') for office employees
   * - editInitialData: Initial data for editing office employee (if in edit mode)
   * 
   * Construction Worker States:
   * - constructionSearchQuery: Current search query for construction workers
   * - constructionSearchResults: Array of construction worker search results
   * - isConstructionSearching: Boolean indicating if construction worker search is in progress
   * - isConstructionModalOpen: Boolean indicating if construction worker modal is open
   * - isConstructionLoading: Boolean indicating if construction worker operation is loading
   * - constructionModalMode: Current modal mode ('add' or 'edit') for construction workers
   * - constructionEditInitialData: Initial data for editing construction worker (if in edit mode)
   */
  const [isMobile, setIsMobile] = useState(false);
  
  // Office employee states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Employee[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editInitialData, setEditInitialData] = useState<
    { empId: string; name: string; userType: string; assignedGenbaName?: string } | undefined
  >(undefined);

  // Construction worker states
  const [constructionSearchQuery, setConstructionSearchQuery] = useState('');
  const [constructionSearchResults, setConstructionSearchResults] = useState<ConstructionWorker[]>([]);
  const [isConstructionSearching, setIsConstructionSearching] = useState(false);
  const [isConstructionModalOpen, setIsConstructionModalOpen] = useState(false);
  const [isConstructionLoading, setIsConstructionLoading] = useState(false);
  const [constructionModalMode, setConstructionModalMode] = useState<'add' | 'edit'>('add');
  const [constructionEditInitialData, setConstructionEditInitialData] = useState<{ empCode: string; name: string; userType: string } | undefined>(undefined);

  /** Full admin (管理者): single list + single register modal */
  const [unifiedSearchQuery, setUnifiedSearchQuery] = useState('');
  const [isUnifiedRegisterOpen, setIsUnifiedRegisterOpen] = useState(false);
  const [isUnifiedSearching, setIsUnifiedSearching] = useState(false);

  /** Delete confirmation modal (本社 / 現場 / 両方) */
  const [deleteModal, setDeleteModal] = useState<null | {
    target: 'office' | 'construction';
    empCode: string;
    dual: boolean;
  }>(null);
  const [deleteModalBusy, setDeleteModalBusy] = useState(false);

  /**
   * Handles office employee search operation.
   * 
   * Searches for office employees by employee ID or name.
   * Updates search results state with found employees or empty array if none found.
   * 
   * @async
   * @function
   * @example
   * ```typescript
   * // Search for employees with query "123456"
   * handleSearch();
   * ```
   */
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await EmployeeService.searchEmployees(searchQuery.trim());
      if (response.success && response.data) {
        setSearchResults(response.data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Handles opening the add new office employee modal.
   * 
   * Stops event propagation to prevent parent elements from intercepting on mobile.
   * Sets modal mode to 'add' and clears any existing edit data.
   * 
   * @param e - Optional mouse or touch event to stop propagation
   * 
   * @example
   * ```typescript
   * // Open add modal
   * handleAddNew();
   * ```
   */
  const handleAddNew = (e?: React.MouseEvent | React.TouchEvent) => {
    // Stop event propagation to prevent any parent elements from intercepting on mobile
    if (e) {
      e.stopPropagation();
    }
    setModalMode('add');
    setEditInitialData(undefined);
    // Use functional update to ensure state change is detected
    setIsModalOpen((prev) => {
      return true;
    });
  };

  /**
   * Handles adding a new office employee.
   * 
   * Creates a new employee with the provided user data.
   * Refreshes search results after successful creation.
   * Shows error alert if creation fails.
   * 
   * @param userData - User data object containing empId, name, userType, and password
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * await handleAddUser({
   *   empId: '123456',
   *   name: '山田太郎',
   *   userType: '従業員',
   *   password: 'Password123'
   * });
   * ```
   */
  const handleAddUser = async (userData: any) => {
    try {
      setIsLoading(true);
      const newEmployee: Employee = {
        empCode: userData.empId,
        name: userData.name,
        userType: userData.userType,
        password: userData.password,
        ...(userData.userType === OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN
          ? { assignedGenbaName: (userData.assignedGenbaName ?? '').trim() }
          : {}),
      };
      
      const response = await EmployeeService.createEmployee(newEmployee);
      if (response.success && response.data) {
        if (!isConstructionAdminOnly) {
          await refreshAllLists();
        } else if (searchQuery.trim()) {
          handleSearch();
        } else {
          loadAllEmployees();
        }
      } else {
        alert('従業員の作成に失敗しました: ' + response.message);
      }
    } catch (error) {
      alert('従業員の作成中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Loads all office employees from the backend.
   * 
   * Fetches all employees and updates the search results state.
   * Used when search query is cleared or on component mount.
   * 
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * // Load all employees
   * await loadAllEmployees();
   * ```
   */
  const loadAllEmployees = async () => {
    try {
      setIsLoading(true);
      const response = await EmployeeService.getAllEmployees();
      if (response.success && response.data) {
        setSearchResults(response.data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles clearing the office employee search.
   * 
   * Clears the search query and loads all employees.
   * 
   * @function
   * 
   * @example
   * ```typescript
   * // Clear search and show all employees
   * handleClearSearch();
   * ```
   */
  const handleClearSearch = () => {
    setSearchQuery('');
    loadAllEmployees();
  };

  /**
   * Handles editing an office employee.
   * 
   * Fetches employee data by employee code and opens the edit modal
   * with the employee's current data pre-filled.
   * 
   * @param empCode - Employee code of the employee to edit
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * // Edit employee with code "123456"
   * await handleEditEmployee('123456');
   * ```
   */
  const handleEditEmployee = async (empCode: string) => {
    try {
      setIsLoading(true);
      const response = await EmployeeService.getEmployeeByEmpCode(empCode);
      if (response.success && response.data) {
        setEditInitialData({
          empId: response.data.empCode,
          name: response.data.name,
          userType: response.data.userType,
          assignedGenbaName: response.data.assignedGenbaName,
        });
        setModalMode('edit');
        setIsModalOpen(true);
      } else {
        alert('従業員情報の取得に失敗しました: ' + (response.message || ''));
      }
    } catch (error) {
      alert('従業員情報の取得中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles updating an office employee.
   * 
   * Updates employee information with the provided user data.
   * Password is optional and only updated if provided.
   * Refreshes search results after successful update.
   * Shows success/error alert based on operation result.
   * 
   * @param userData - User data object containing empId, name, userType, and optional password
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * await handleUpdateUser({
   *   empId: '123456',
   *   name: '山田太郎',
   *   userType: '管理者',
   *   password: 'NewPassword123' // Optional
   * });
   * ```
   */
  const handleUpdateUser = async (userData: any) => {
    try {
      setIsLoading(true);
      const updatedEmployee: Employee = {
        empCode: userData.empId,
        name: userData.name,
        userType: userData.userType,
        ...(userData.userType === OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN
          ? { assignedGenbaName: (userData.assignedGenbaName ?? '').trim() }
          : {}),
      };
      if (userData.password && userData.password.trim().length > 0) {
        updatedEmployee.password = userData.password;
        if (userData.currentPassword && userData.currentPassword.trim().length > 0) {
          updatedEmployee.currentPassword = userData.currentPassword.trim();
        }
      }

      const response = await EmployeeService.updateEmployee(userData.empId, updatedEmployee);
      if (response.success && response.data) {
        if (!isConstructionAdminOnly) {
          await refreshAllLists();
        } else if (searchQuery.trim()) {
          await handleSearch();
        } else {
          await loadAllEmployees();
        }
        alert('従業員情報を更新しました');
      } else {
        alert('従業員情報の更新に失敗しました: ' + (response.message || ''));
      }
    } catch (error) {
      alert('従業員情報の更新中にエラーが発生しました');
    } finally {
      setIsLoading(false);
      setModalMode('add');
      setEditInitialData(undefined);
    }
  };

  /**
   * Handles deleting an office employee.
   * 
   * Deletes an employee by employee code.
   * Refreshes search results after successful deletion.
   * Shows success/error alert based on operation result.
   * 
   * Note: This operation is irreversible. Confirmation should be handled
   * by the calling component (SearchResults).
   * 
   * @param empCode - Employee code of the employee to delete
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * // Delete employee with code "123456"
   * await handleDeleteEmployee('123456');
   * ```
   */
  /**
   * Handles construction worker search operation.
   * 
   * Searches for construction workers by employee code or name.
   * Updates search results state with found workers or empty array if none found.
   * 
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * // Search for construction workers with query "C12345"
   * handleConstructionSearch();
   * ```
   */
  const handleConstructionSearch = async () => {
    if (!constructionSearchQuery.trim()) return;
    
    setIsConstructionSearching(true);
    try {
      const response = await ConstructionWorkerService.searchConstructionWorkers(constructionSearchQuery.trim());
      if (response.success && response.data) {
        setConstructionSearchResults(response.data);
      } else {
        setConstructionSearchResults([]);
      }
    } catch (error) {
      setConstructionSearchResults([]);
    } finally {
      setIsConstructionSearching(false);
    }
  };

  /**
   * Handles opening the add new construction worker modal.
   * 
   * Stops event propagation to prevent parent elements from intercepting on mobile.
   * Sets modal mode to 'add' and clears any existing edit data.
   * 
   * @param e - Optional mouse or touch event to stop propagation
   * 
   * @example
   * ```typescript
   * // Open add modal
   * handleConstructionAddNew();
   * ```
   */
  const handleConstructionAddNew = (e?: React.MouseEvent | React.TouchEvent) => {
    // Stop event propagation to prevent any parent elements from intercepting on mobile.
    // Do not use preventDefault() here—touch events are passive and it triggers a console warning.
    if (e) {
      e.stopPropagation();
    }
    setConstructionModalMode('add');
    setConstructionEditInitialData(undefined);
    setIsConstructionModalOpen(true);
  };

  /**
   * Handles adding a new construction worker.
   * 
   * Creates a new construction worker with the provided user data.
   * Refreshes search results after successful creation.
   * Shows error alert if creation fails.
   * 
   * @param userData - User data object containing empCode, name, and password
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * await handleConstructionAddUser({
   *   empCode: 'C12345',
   *   name: '田中花子',
   *   password: 'Password123'
   * });
   * ```
   */
  const handleConstructionAddUser = async (userData: any): Promise<{ success: boolean; message?: string }> => {
    try {
      setIsConstructionLoading(true);
      const newEmployee: Employee = {
        empCode: userData.empCode,
        name: userData.name,
        userType: '従業員',
        password: userData.password,
      };

      const res = await EmployeeService.createEmployee(newEmployee);
      if (!res.success || !res.data) {
        return { success: false, message: res.message || '本社の登録に失敗しました' };
      }

      const newConstructionWorker: ConstructionWorker = {
        empCode: userData.empCode,
        name: userData.name,
        password: userData.password,
      };

      const response = await ConstructionWorkerService.createConstructionWorker(newConstructionWorker);
      if (response.success && response.data) {
        if (!isConstructionAdminOnly) {
          await refreshAllLists();
        } else if (constructionSearchQuery.trim()) {
          handleConstructionSearch();
        } else {
          loadAllConstructionWorkers();
        }
        return { success: true };
      }
      const msg = response.message
        ? `本社は登録しましたが、現場の登録に失敗しました: ${response.message}`
        : '本社は登録しましたが、現場の登録に失敗しました';
      if (!isConstructionAdminOnly) {
        await refreshAllLists();
      } else if (constructionSearchQuery.trim()) {
        handleConstructionSearch();
      } else {
        loadAllConstructionWorkers();
      }
      return { success: false, message: msg };
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || '現場の作成中にエラーが発生しました';
      return { success: false, message };
    } finally {
      setIsConstructionLoading(false);
    }
  };

  /**
   * Loads all construction workers from the backend.
   * 
   * Fetches all construction workers and updates the search results state.
   * Used when search query is cleared or on component mount.
   * 
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * // Load all construction workers
   * await loadAllConstructionWorkers();
   * ```
   */
  const loadAllConstructionWorkers = async () => {
    try {
      setIsConstructionLoading(true);
      const response = await ConstructionWorkerService.getAllConstructionWorkers();
      if (response.success && response.data) {
        setConstructionSearchResults(response.data);
      } else {
        setConstructionSearchResults([]);
      }
    } catch (error) {
      setConstructionSearchResults([]);
    } finally {
      setIsConstructionLoading(false);
    }
  };

  const refreshAllLists = async () => {
    await loadAllEmployees();
    await loadAllConstructionWorkers();
  };

  /** After unified registration or error recovery — 現場管理者は本社一覧を再取得しない */
  const reloadUnifiedViewAfterMutation = async () => {
    if (isConstructionAdminOnly) {
      const q = unifiedSearchQuery.trim();
      if (q) {
        setIsUnifiedSearching(true);
        setIsConstructionSearching(true);
        try {
          const cwRes = await ConstructionWorkerService.searchConstructionWorkers(q);
          setConstructionSearchResults(cwRes.success && cwRes.data ? cwRes.data : []);
        } catch {
          setConstructionSearchResults([]);
        } finally {
          setIsUnifiedSearching(false);
          setIsConstructionSearching(false);
        }
      } else {
        await loadAllConstructionWorkers();
      }
      return;
    }
    await refreshAllLists();
  };

  const openDeleteEmployeeModal = async (empCode: string) => {
    setDeleteModalBusy(true);
    try {
      const statusRes = await ConstructionWorkerService.getAccountLinkStatus(empCode);
      if (!statusRes.success) {
        alert(statusRes.message || 'リンク状態の取得に失敗しました');
        return;
      }
      const dual = Boolean(statusRes.office && statusRes.construction);
      setDeleteModal({ target: 'office', empCode, dual });
    } catch {
      alert('リンク状態の取得中にエラーが発生しました');
    } finally {
      setDeleteModalBusy(false);
    }
  };

  const openDeleteConstructionModal = async (empCode: string) => {
    setDeleteModalBusy(true);
    try {
      const statusRes = await ConstructionWorkerService.getAccountLinkStatus(empCode);
      if (!statusRes.success) {
        alert(statusRes.message || 'リンク状態の取得に失敗しました');
        return;
      }
      const dual = Boolean(statusRes.office && statusRes.construction);
      setDeleteModal({ target: 'construction', empCode, dual });
    } catch {
      alert('リンク状態の取得中にエラーが発生しました');
    } finally {
      setDeleteModalBusy(false);
    }
  };

  const executeOfficeDelete = async (deleteLinkedConstruction: boolean) => {
    if (!deleteModal || deleteModal.target !== 'office') return;
    const { empCode } = deleteModal;
    setDeleteModalBusy(true);
    setIsLoading(true);
    try {
      const response = await EmployeeService.deleteEmployee(empCode, deleteLinkedConstruction);
      if (!response.success) {
        alert('削除に失敗しました: ' + (response.message || ''));
        return;
      }
      if (!isConstructionAdminOnly) {
        await refreshAllLists();
      } else if (searchQuery.trim()) {
        handleSearch();
      } else {
        loadAllEmployees();
      }
      alert(`従業員コード「${empCode}」を削除しました`);
      setDeleteModal(null);
    } catch {
      alert('従業員の削除中にエラーが発生しました');
    } finally {
      setDeleteModalBusy(false);
      setIsLoading(false);
    }
  };

  const executeConstructionDelete = async (deleteLinkedOffice: boolean) => {
    if (!deleteModal || deleteModal.target !== 'construction') return;
    const { empCode } = deleteModal;
    setDeleteModalBusy(true);
    setIsConstructionLoading(true);
    try {
      const response = await ConstructionWorkerService.deleteConstructionWorker(empCode, deleteLinkedOffice);
      if (!response.success) {
        alert('削除に失敗しました: ' + (response.message || ''));
        return;
      }
      if (!isConstructionAdminOnly) {
        await refreshAllLists();
      } else if (constructionSearchQuery.trim()) {
        handleConstructionSearch();
      } else {
        loadAllConstructionWorkers();
      }
      alert(`現場コード「${empCode}」を削除しました`);
      setDeleteModal(null);
    } catch {
      alert('現場の削除中にエラーが発生しました');
    } finally {
      setDeleteModalBusy(false);
      setIsConstructionLoading(false);
    }
  };

  /** Passed to search results — opens the delete confirmation modal */
  const handleDeleteEmployee = openDeleteEmployeeModal;
  const handleConstructionDeleteWorker = openDeleteConstructionModal;

  const handleUnifiedSearch = async () => {
    const q = unifiedSearchQuery.trim();
    if (!q) return;
    if (isConstructionAdminOnly) {
      setConstructionSearchQuery(q);
      setIsUnifiedSearching(true);
      setIsConstructionSearching(true);
      try {
        const cwRes = await ConstructionWorkerService.searchConstructionWorkers(q);
        setConstructionSearchResults(cwRes.success && cwRes.data ? cwRes.data : []);
      } catch {
        setConstructionSearchResults([]);
      } finally {
        setIsUnifiedSearching(false);
        setIsConstructionSearching(false);
      }
      return;
    }
    setSearchQuery(q);
    setConstructionSearchQuery(q);
    setIsUnifiedSearching(true);
    setIsSearching(true);
    setIsConstructionSearching(true);
    try {
      const [empRes, cwRes] = await Promise.all([
        EmployeeService.searchEmployees(q),
        ConstructionWorkerService.searchConstructionWorkers(q),
      ]);
      setSearchResults(empRes.success && empRes.data ? empRes.data : []);
      setConstructionSearchResults(cwRes.success && cwRes.data ? cwRes.data : []);
    } catch {
      setSearchResults([]);
      setConstructionSearchResults([]);
    } finally {
      setIsUnifiedSearching(false);
      setIsSearching(false);
      setIsConstructionSearching(false);
    }
  };

  const handleUnifiedClear = () => {
    setUnifiedSearchQuery('');
    if (isConstructionAdminOnly) {
      setConstructionSearchQuery('');
      loadAllConstructionWorkers();
      return;
    }
    setSearchQuery('');
    setConstructionSearchQuery('');
    loadAllEmployees();
    loadAllConstructionWorkers();
  };

  const handleUnifiedRegisterSubmit = async (
    data: UnifiedRegisterFormData
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      setIsLoading(true);
      const newEmployee: Employee = {
        empCode: data.empCode,
        name: data.name,
        userType: data.officeUserType,
        password: data.password,
        ...(data.officeUserType === OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN && data.assignedGenbaName
          ? { assignedGenbaName: data.assignedGenbaName }
          : {}),
      };
      const res = await EmployeeService.createEmployee(newEmployee);
      if (!res.success || !res.data) {
        return { success: false, message: res.message || '本社の登録に失敗しました' };
      }

      const registerConstruction = data.officeUserType === '従業員';
      if (registerConstruction) {
        const cw: ConstructionWorker = {
          empCode: data.empCode,
          name: data.name,
          password: data.password,
        };
        const cwRes = await ConstructionWorkerService.createConstructionWorker(cw);
        if (!cwRes.success || !cwRes.data) {
          const msg = cwRes.message
            ? `本社は登録しましたが、現場の登録に失敗しました: ${cwRes.message}`
            : '本社は登録しましたが、現場の登録に失敗しました';
          await reloadUnifiedViewAfterMutation();
          return { success: false, message: msg };
        }
      }

      await reloadUnifiedViewAfterMutation();
      alert('登録しました');
      return { success: true };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '登録中にエラーが発生しました';
      return { success: false, message: msg };
    } finally {
      setIsLoading(false);
    }
  };

  const mergedUnifiedRows: UnifiedUserRow[] = useMemo(() => {
    const map = new Map<string, UnifiedUserRow>();

    if (isConstructionAdminOnly) {
      for (const w of constructionSearchResults) {
        map.set(w.empCode, {
          empCode: w.empCode,
          name: w.name,
          roleLabel: '従業員',
          hasOffice: false,
          hasConstruction: true,
        });
      }
      const rows = Array.from(map.values());
      rows.sort((a, b) => a.empCode.localeCompare(b.empCode, 'ja'));
      return rows;
    }

    for (const e of searchResults) {
      map.set(e.empCode, {
        empCode: e.empCode,
        name: e.name,
        roleLabel: e.userType,
        assignedGenbaName: e.assignedGenbaName,
        hasOffice: true,
        hasConstruction: false,
      });
    }
    for (const w of constructionSearchResults) {
      const existing = map.get(w.empCode);
      if (existing) {
        existing.hasConstruction = true;
      } else {
        map.set(w.empCode, {
          empCode: w.empCode,
          name: w.name,
          roleLabel: '従業員',
          hasOffice: false,
          hasConstruction: true,
        });
      }
    }

    const rows = Array.from(map.values());
    rows.sort((a, b) => a.empCode.localeCompare(b.empCode, 'ja'));
    return rows;
  }, [isConstructionAdminOnly, searchResults, constructionSearchResults]);

  /**
   * Handles clearing the construction worker search.
   * 
   * Clears the search query and loads all construction workers.
   * 
   * @function
   * 
   * @example
   * ```typescript
   * // Clear search and show all construction workers
   * handleConstructionClearSearch();
   * ```
   */
  const handleConstructionClearSearch = () => {
    setConstructionSearchQuery('');
    loadAllConstructionWorkers();
  };

  /**
   * Handles editing a construction worker.
   * 
   * Fetches construction worker data by employee code and opens the edit modal
   * with the worker's current data pre-filled.
   * 
   * @param empCode - Employee code of the construction worker to edit
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * // Edit construction worker with code "C12345"
   * await handleConstructionEditWorker('C12345');
   * ```
   */
  const handleConstructionEditWorker = async (empCode: string) => {
    try {
      setIsConstructionLoading(true);
      const response = await ConstructionWorkerService.getConstructionWorkerByEmpCode(empCode);
      if (response.success && response.data) {
        setConstructionEditInitialData({
          empCode: response.data.empCode,
          name: response.data.name,
          userType: 'construction worker',
        });
        setConstructionModalMode('edit');
        setIsConstructionModalOpen(true);
      } else {
        alert('現場情報の取得に失敗しました: ' + (response.message || ''));
      }
    } catch (error) {
      alert('現場情報の取得中にエラーが発生しました');
    } finally {
      setIsConstructionLoading(false);
    }
  };

  /**
   * Handles updating a construction worker.
   * 
   * Updates construction worker information with the provided user data.
   * Password is optional and only updated if provided.
   * Refreshes search results after successful update.
   * Shows success/error alert based on operation result.
   * 
   * @param userData - User data object containing empCode, name, and optional password
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * await handleConstructionUpdateUser({
   *   empCode: 'C12345',
   *   name: '田中花子',
   *   password: 'NewPassword123' // Optional
   * });
   * ```
   */
  const handleConstructionUpdateUser = async (userData: any): Promise<{ success: boolean; message?: string }> => {
    try {
      setIsConstructionLoading(true);
      const updatedConstructionWorker: ConstructionWorker = {
        empCode: userData.empCode,
        name: userData.name,
      };
      if (userData.password && userData.password.trim().length > 0) {
        updatedConstructionWorker.password = userData.password;
      }

      const response = await ConstructionWorkerService.updateConstructionWorker(userData.empCode, updatedConstructionWorker);
      if (response.success && response.data) {
        if (!isConstructionAdminOnly) {
          await refreshAllLists();
        } else if (constructionSearchQuery.trim()) {
          await handleConstructionSearch();
        } else {
          await loadAllConstructionWorkers();
        }
        setConstructionModalMode('add');
        setConstructionEditInitialData(undefined);
        return { success: true };
      }
      return { success: false, message: response.message || '現場情報の更新に失敗しました' };
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || '現場情報の更新中にエラーが発生しました';
      return { success: false, message };
    } finally {
      setIsConstructionLoading(false);
    }
  };

  /**
   * Handles deleting a construction worker.
   * 
   * Deletes a construction worker by employee code.
   * Refreshes search results after successful deletion.
   * Shows success/error alert based on operation result.
   * 
   * Note: This operation is irreversible. Confirmation should be handled
   * by the calling component (ConstructionWorkerSearchResults).
   * 
   * @param empCode - Employee code of the construction worker to delete
   * @async
   * @function
   * 
   * @example
   * ```typescript
   * // Delete construction worker with code "C12345"
   * await handleConstructionDeleteWorker('C12345');
   * ```
   */
  /**
   * Check for mobile device and load all employees/workers on mount.
   * 
   * Detects mobile devices based on window width (<= 768px) and sets
   * the isMobile state accordingly. Sets up resize listener to update
   * mobile state when window is resized. Loads all employees and construction
   * workers on component mount.
   * 
   * @effect
   * Runs once on mount.
   */
  useEffect(() => {
    /**
     * Check if device is mobile based on window width.
     * Mobile threshold: 768px (tablet and below).
     */
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    /**
     * Initial mobile check on mount.
     */
    checkMobile();
    
    /**
     * Add resize listener to update mobile state on window resize.
     */
    window.addEventListener('resize', checkMobile);
    
    /**
     * Load lists on mount (現場管理者は現場APIのみ — 本社一覧は権限外).
     */
    if (!isConstructionAdminOnly) {
      loadAllEmployees();
    }
    loadAllConstructionWorkers();
    
    /**
     * Cleanup: Remove resize listener on unmount.
     */
    return () => window.removeEventListener('resize', checkMobile);
  }, [isConstructionAdminOnly]);

  const deleteModalElement = (
    <DeleteConfirmModal
      isOpen={deleteModal !== null}
      target={deleteModal?.target ?? 'office'}
      empCode={deleteModal?.empCode ?? ''}
      dual={deleteModal?.dual ?? false}
      isSubmitting={deleteModalBusy}
      onClose={() => {
        if (!deleteModalBusy) setDeleteModal(null);
      }}
      onConfirm={() => {
        if (!deleteModal) return;
        const deleteLinked = deleteModal.dual;
        if (deleteModal.target === 'office') void executeOfficeDelete(deleteLinked);
        else void executeConstructionDelete(deleteLinked);
      }}
    />
  );

  /**
   * Full admin (管理者): single list + unified registration modal.
   */
  if (!isConstructionAdminOnly) {
    return (
      <div className={isMobile ? 'main-content mobile-layout' : 'main-content'}>
        <h3>従業員管理</h3>
        <div className="tab-container">
          <div className="user-management-container">
            <div className="search-section">
              <div className="search-header">
                <div className="header-content">
                  <div className="header-text">
                    <h4>従業員検索</h4>
                    <p>本社・現場の従業員を一覧で検索・登録できます</p>
                  </div>
                  <button type="button" className="add-new-button" onClick={() => setIsUnifiedRegisterOpen(true)}>
                    ➕ 新規追加
                  </button>
                </div>
              </div>
              <div className="search-controls">
                <div className="search-input-group">
                  <input
                    type="text"
                    placeholder="従業員IDまたは名前を入力..."
                    value={unifiedSearchQuery}
                    onChange={(e) => setUnifiedSearchQuery(e.target.value)}
                    className="search-input"
                    onKeyPress={(e) => e.key === 'Enter' && handleUnifiedSearch()}
                  />
                  <button
                    type="button"
                    className="search-button"
                    onClick={handleUnifiedSearch}
                    disabled={!unifiedSearchQuery.trim() || isUnifiedSearching}
                  >
                    {isUnifiedSearching ? '検索中...' : '🔍 検索'}
                  </button>
                  <button type="button" className="clear-button" onClick={handleUnifiedClear} disabled={isUnifiedSearching}>
                    🗑️ クリア
                  </button>
                </div>
              </div>
            </div>
            <div className="results-section">
              <UnifiedUserSearchResults
                rows={mergedUnifiedRows}
                searchQuery={unifiedSearchQuery}
                isSearching={isUnifiedSearching}
                onEditOffice={handleEditEmployee}
                onEditConstruction={handleConstructionEditWorker}
                onDeleteOffice={handleDeleteEmployee}
                onDeleteConstruction={handleConstructionDeleteWorker}
              />
            </div>
          </div>
        </div>

        <UnifiedRegisterModal
          isOpen={isUnifiedRegisterOpen}
          onClose={() => setIsUnifiedRegisterOpen(false)}
          onSubmit={handleUnifiedRegisterSubmit}
        />
        <AddNewUserModal
          key={`modal-${isModalOpen}-${modalMode}`}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onAddUser={handleAddUser}
          onUpdateUser={handleUpdateUser}
          mode={modalMode}
          initialData={editInitialData}
        />
        <ConstructionWorkerModal
          isOpen={isConstructionModalOpen}
          onClose={() => setIsConstructionModalOpen(false)}
          onAddUser={handleConstructionAddUser}
          onUpdateUser={handleConstructionUpdateUser}
          mode={constructionModalMode}
          initialData={constructionEditInitialData}
        />
        {deleteModalElement}
      </div>
    );
  }

  /**
   * 現場管理者: 管理者と同じ単一カラム（検索・一覧・新規）— 本社/現場タブなし。
   */
  return (
    <div className={isMobile ? 'main-content mobile-layout' : 'main-content'}>
      <h3>従業員管理</h3>
      <div className="tab-container">
        <div className="user-management-container">
          <div className="search-section">
            <div className="search-header">
              <div className="header-content">
                <div className="header-text">
                  <h4>従業員検索</h4>
                  <p>現場の従業員を一覧で検索・登録できます</p>
                </div>
                <button type="button" className="add-new-button" onClick={() => setIsUnifiedRegisterOpen(true)}>
                  ➕ 新規追加
                </button>
              </div>
            </div>
            <div className="search-controls">
              <div className="search-input-group">
                <input
                  type="text"
                  placeholder="従業員IDまたは名前を入力..."
                  value={unifiedSearchQuery}
                  onChange={(e) => setUnifiedSearchQuery(e.target.value)}
                  className="search-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleUnifiedSearch()}
                />
                <button
                  type="button"
                  className="search-button"
                  onClick={handleUnifiedSearch}
                  disabled={!unifiedSearchQuery.trim() || isUnifiedSearching}
                >
                  {isUnifiedSearching ? '検索中...' : '🔍 検索'}
                </button>
                <button type="button" className="clear-button" onClick={handleUnifiedClear} disabled={isUnifiedSearching}>
                  🗑️ クリア
                </button>
              </div>
            </div>
          </div>
          <div className="results-section">
            <UnifiedUserSearchResults
              rows={mergedUnifiedRows}
              searchQuery={unifiedSearchQuery}
              isSearching={isUnifiedSearching}
              onEditOffice={handleConstructionEditWorker}
              onEditConstruction={handleConstructionEditWorker}
              onDeleteOffice={handleConstructionDeleteWorker}
              onDeleteConstruction={handleConstructionDeleteWorker}
              constructionAdminOnly
            />
          </div>
        </div>
      </div>

      <UnifiedRegisterModal
        isOpen={isUnifiedRegisterOpen}
        onClose={() => setIsUnifiedRegisterOpen(false)}
        onSubmit={handleUnifiedRegisterSubmit}
        variant="constructionAdmin"
      />
      <ConstructionWorkerModal
        isOpen={isConstructionModalOpen}
        onClose={() => setIsConstructionModalOpen(false)}
        onAddUser={handleConstructionAddUser}
        onUpdateUser={handleConstructionUpdateUser}
        mode={constructionModalMode}
        initialData={constructionEditInitialData}
      />
      {deleteModalElement}
    </div>
  );
} 